import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { OrgEntityColors } from '@newgenus/common';
import { DEFAULT_ENTITY_COLORS, OrgEntityType } from '../services/org-entity-colors.service';

export interface EntityColorPickerDialogData {
    currentColors: OrgEntityColors | undefined;
}

export interface EntityColorPickerDialogResult {
    colors: OrgEntityColors;
}

interface ColorEntry {
    key: OrgEntityType;
    label: string;
    icon: string;
    color: string;
}

@Component({
    selector: 'shared-entity-color-picker-dialog',
    imports: [CommonModule, FormsModule, MatDialogModule, MatIconModule, MatButtonModule, MatTooltipModule],
    template: `
        <h2 mat-dialog-title class="dialog-title">
            <mat-icon>palette</mat-icon>
            Entity Color Theme
            <button mat-icon-button mat-dialog-close class="close-btn">
                <mat-icon>close</mat-icon>
            </button>
        </h2>

        <mat-dialog-content class="color-picker-content">
            <p class="description">
                Customize the icon and badge colors for each entity type across your organization dashboard.
            </p>

            <div class="color-entries">
                <div class="color-entry" *ngFor="let entry of entries">
                    <div class="entry-preview">
                        <mat-icon [style.color]="entry.color">{{ entry.icon }}</mat-icon>
                    </div>
                    <span class="entry-label">{{ entry.label }}</span>
                    <div class="color-controls">
                        <label
                            class="color-swatch"
                            [style.background]="entry.color"
                            [matTooltip]="'Click to change color'"
                        >
                            <input type="color" [value]="entry.color" (input)="onColorChange(entry, $event)" />
                        </label>
                        <span class="color-hex">{{ entry.color }}</span>
                        <button
                            mat-icon-button
                            class="reset-btn"
                            [matTooltip]="'Reset to default'"
                            [disabled]="entry.color === getDefault(entry.key)"
                            (click)="resetColor(entry)"
                        >
                            <mat-icon>restart_alt</mat-icon>
                        </button>
                    </div>
                </div>
            </div>
        </mat-dialog-content>

        <mat-dialog-actions align="end">
            <button mat-button (click)="resetAll()">
                <mat-icon>settings_backup_restore</mat-icon>
                Reset All
            </button>
            <button mat-button mat-dialog-close>Cancel</button>
            <button mat-raised-button color="primary" (click)="save()">
                <mat-icon>save</mat-icon>
                Save
            </button>
        </mat-dialog-actions>
    `,
    styles: [
        `
            :host {
                overflow: hidden;
            }

            .dialog-title {
                display: flex;
                align-items: center;
                gap: 8px;
                position: relative;
                mat-icon {
                    color: #ab47bc;
                }
            }

            .close-btn {
                position: absolute;
                top: 8px;
                right: 8px;
                width: 28px;
                height: 28px;
                background: rgba(0, 0, 0, 0.06);
                border-radius: 50%;
                display: flex;
                align-items: center;
                justify-content: center;
                padding: 0;
                opacity: 0.6;
                transition:
                    opacity 0.2s,
                    background 0.2s;

                &:hover {
                    opacity: 1;
                    background: rgba(0, 0, 0, 0.12);
                }

                mat-icon {
                    font-size: 14px;
                    width: 14px;
                    height: 14px;
                    color: rgba(0, 0, 0, 0.6);
                }
            }

            .color-picker-content {
                min-width: 400px;
                max-width: 500px;
                overflow-x: hidden;
            }

            .description {
                color: rgba(0, 0, 0, 0.54);
                font-size: 13px;
                margin-bottom: 20px;
            }

            .color-entries {
                display: flex;
                flex-direction: column;
                gap: 12px;
            }

            .color-entry {
                display: flex;
                align-items: center;
                gap: 12px;
                padding: 8px 12px;
                border-radius: 8px;
                background: rgba(0, 0, 0, 0.03);
                transition: background 0.2s;

                &:hover {
                    background: rgba(0, 0, 0, 0.06);
                }
            }

            .entry-preview {
                width: 36px;
                height: 36px;
                display: flex;
                align-items: center;
                justify-content: center;
                border-radius: 50%;
                background: rgba(0, 0, 0, 0.04);
                flex-shrink: 0;

                mat-icon {
                    font-size: 22px;
                    width: 22px;
                    height: 22px;
                }
            }

            .entry-label {
                flex: 1;
                font-size: 14px;
                font-weight: 500;
                color: rgba(0, 0, 0, 0.87);
            }

            .color-controls {
                display: flex;
                align-items: center;
                gap: 8px;
            }

            .color-swatch {
                width: 32px;
                height: 32px;
                border-radius: 50%;
                border: 2px solid rgba(0, 0, 0, 0.15);
                cursor: pointer;
                position: relative;
                overflow: hidden;
                transition:
                    border-color 0.2s,
                    transform 0.15s;

                &:hover {
                    border-color: rgba(0, 0, 0, 0.35);
                    transform: scale(1.1);
                }

                input[type='color'] {
                    position: absolute;
                    top: -10px;
                    left: -10px;
                    width: 60px;
                    height: 60px;
                    opacity: 0;
                    cursor: pointer;
                }
            }

            .color-hex {
                font-family: monospace;
                font-size: 12px;
                color: rgba(0, 0, 0, 0.45);
                width: 65px;
            }

            .reset-btn {
                opacity: 0.5;
                &:hover {
                    opacity: 1;
                }
                mat-icon {
                    font-size: 18px;
                    width: 18px;
                    height: 18px;
                }
            }

            /* Dark theme overrides */
            :host-context(.dark-theme) {
                .close-btn {
                    background: rgba(255, 255, 255, 0.1);
                    &:hover {
                        background: rgba(255, 255, 255, 0.2);
                    }
                    mat-icon {
                        color: rgba(255, 255, 255, 0.8);
                    }
                }
                .description {
                    color: rgba(255, 255, 255, 0.6);
                }
                .color-entry {
                    background: rgba(255, 255, 255, 0.04);
                    &:hover {
                        background: rgba(255, 255, 255, 0.08);
                    }
                }
                .entry-preview {
                    background: rgba(255, 255, 255, 0.06);
                }
                .entry-label {
                    color: rgba(255, 255, 255, 0.87);
                }
                .color-swatch {
                    border-color: rgba(255, 255, 255, 0.2);
                    &:hover {
                        border-color: rgba(255, 255, 255, 0.5);
                    }
                }
                .color-hex {
                    color: rgba(255, 255, 255, 0.5);
                }
            }
        `,
    ],
})
export class EntityColorPickerDialogComponent {
    public entries: ColorEntry[] = [
        { key: 'users', label: 'Staff', icon: 'people', color: '' },
        { key: 'hats', label: 'Hats', icon: 'school', color: '' },
        { key: 'positions', label: 'Org structure parts', icon: 'account_tree', color: '' },
        { key: 'securityGroups', label: 'Security Groups', icon: 'security', color: '' },
        { key: 'policies', label: 'Policies', icon: 'policy', color: '' },
        { key: 'features', label: 'Features', icon: 'extension', color: '' },
        { key: 'permissions', label: 'Permissions', icon: 'vpn_key', color: '' },
    ];

    constructor(
        private dialogRef: MatDialogRef<EntityColorPickerDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public data: EntityColorPickerDialogData
    ) {
        // Initialize entries with current or default colors
        const current = data.currentColors || {};
        for (const entry of this.entries) {
            entry.color = current[entry.key] || DEFAULT_ENTITY_COLORS[entry.key];
        }
    }

    public getDefault(key: OrgEntityType): string {
        return DEFAULT_ENTITY_COLORS[key];
    }

    public onColorChange(entry: ColorEntry, event: Event): void {
        entry.color = (event.target as HTMLInputElement).value;
    }

    public resetColor(entry: ColorEntry): void {
        entry.color = DEFAULT_ENTITY_COLORS[entry.key];
    }

    public resetAll(): void {
        for (const entry of this.entries) {
            entry.color = DEFAULT_ENTITY_COLORS[entry.key];
        }
    }

    public save(): void {
        const colors: OrgEntityColors = {};
        for (const entry of this.entries) {
            // Only store non-default colors to keep the document lean
            if (entry.color !== DEFAULT_ENTITY_COLORS[entry.key]) {
                colors[entry.key] = entry.color;
            }
        }
        this.dialogRef.close({ colors } as EntityColorPickerDialogResult);
    }
}
